import { EditorState, Extension, RangeSetBuilder, StateField } from '@codemirror/state';
import { Decoration, DecorationSet, EditorView } from '@codemirror/view';

/** CSS class applied to every highlighted occurrence of a variable under the cursor */
export const VARIABLE_HIGHLIGHT_CLASS = 'formula-highlight-match';

/** CSS class applied to every known variable occurrence regardless of cursor position */
export const VARIABLE_DEFAULT_HIGHLIGHT_CLASS = 'formula-highlight-default';

/** Mark decoration for the always-on default variable highlight */
const defaultHighlightDecoration = Decoration.mark({ class: VARIABLE_DEFAULT_HIGHLIGHT_CLASS });

/** Mark decoration that wraps each matching variable span */
const highlightDecoration = Decoration.mark({ class: VARIABLE_HIGHLIGHT_CLASS });

/**
 * Escapes characters that are special inside a `RegExp` constructor string.
 * Hyphen is intentionally NOT escaped – it is safe as a literal outside character classes.
 */
function escapeRegex(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Returns the known-variable name located at the cursor position in the editor state,
 * or `null` when:
 * – the selection spans more than one character (user is actively selecting), or
 * – the word under the cursor is not in the provided `variableNames` set.
 *
 * Identifier characters allowed inside variable names: `[a-zA-Z0-9_-]`
 * (hyphenated names such as `my-var` are first-class in the formula language).
 */
function getVariableAtCursor(state: EditorState, variableNames: ReadonlySet<string>): string | null {
  const sel = state.selection.main;

  // Only act when the cursor is a single point (no active selection)
  if (!sel.empty) {
    return null;
  }

  const pos = sel.head;
  const doc = state.doc.toString();

  let start = pos;
  let end = pos;

  // Expand leftward through identifier characters
  while (start > 0 && /[a-zA-Z0-9_-]/.test(doc[start - 1])) {
    start--;
  }
  // Expand rightward through identifier characters
  while (end < doc.length && /[a-zA-Z0-9_-]/.test(doc[end])) {
    end++;
  }

  if (start === end) {
    return null;
  }

  const word = doc.slice(start, end);

  // Variable names must start with a letter or underscore (formula language spec)
  if (!/^[a-zA-Z_]/.test(word)) {
    return null;
  }

  return variableNames.has(word) ? word : null;
}

/**
 * Returns the positions of every non-overlapping, word-boundary-safe occurrence
 * of `word` inside `doc`.
 *
 * A match is considered word-boundary-safe when neither the immediately preceding
 * nor the immediately following character is an identifier character (`[a-zA-Z0-9_-]`).
 * This prevents a short variable name from matching inside a longer one (e.g. `my`
 * must not match inside `my-sensor`).
 */
function findAllOccurrences(doc: string, word: string): { from: number; to: number }[] {
  const occurrences: { from: number; to: number }[] = [];
  // Negative lookbehind / lookahead prevent partial matches of hyphenated identifiers
  const pattern = new RegExp(`(?<![a-zA-Z0-9_-])${escapeRegex(word)}(?![a-zA-Z0-9_-])`, 'g');
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(doc)) !== null) {
    occurrences.push({ from: match.index, to: match.index + word.length });
  }

  return occurrences;
}

/**
 * Builds a `DecorationSet` that marks every occurrence of every variable in
 * `variableNames` found inside `doc`.  Ranges are collected first, sorted by
 * start position, and then fed to `RangeSetBuilder` (which requires ascending order).
 */
function buildDefaultDecorations(doc: string, variableNames: ReadonlySet<string>): DecorationSet {
  const ranges: { from: number; to: number }[] = [];

  for (const varName of variableNames) {
    for (const occ of findAllOccurrences(doc, varName)) {
      ranges.push(occ);
    }
  }

  // RangeSetBuilder requires ranges to be added in ascending document order
  ranges.sort((a, b) => a.from - b.from);

  const builder = new RangeSetBuilder<Decoration>();
  for (const { from, to } of ranges) {
    builder.add(from, to, defaultHighlightDecoration);
  }

  return builder.finish();
}

/**
 * Creates a CodeMirror `StateField` extension that highlights **all occurrences** of the
 * known variable currently under the cursor.
 *
 * Behaviour:
 * – Decorations are recomputed on every doc change or selection change.
 * – All occurrences (including the one under the cursor) receive the
 *   `formula-highlight-match` CSS class.
 * – Decorations are cleared when the cursor is not on a known variable or
 *   when the user has an active multi-character selection.
 *
 * @param variableNames - The set of known variable names (same set used by the language parser).
 */
export function variableOccurrenceHighlight(variableNames: ReadonlySet<string>): Extension {
  return StateField.define<DecorationSet>({
    create(): DecorationSet {
      return Decoration.none;
    },

    update(decorations, tr): DecorationSet {
      // Nothing changed that would affect highlights → reuse previous decorations
      if (!tr.docChanged && !tr.selection) {
        return decorations;
      }

      const word = getVariableAtCursor(tr.state, variableNames);
      if (!word) {
        return Decoration.none;
      }

      const doc = tr.state.doc.toString();
      const occurrences = findAllOccurrences(doc, word);

      if (occurrences.length === 0) {
        return Decoration.none;
      }

      // RangeSetBuilder requires ranges to be added in document order (from < to)
      const builder = new RangeSetBuilder<Decoration>();
      for (const { from, to } of occurrences) {
        builder.add(from, to, highlightDecoration);
      }

      return builder.finish();
    },

    provide(field): Extension {
      return EditorView.decorations.from(field);
    }
  });
}

/**
 * Creates a CodeMirror `StateField` extension that **always** decorates every
 * occurrence of every known variable in the document with the
 * `formula-highlight-default` CSS class.
 *
 * Behaviour:
 * – Active from the moment the editor loads (no cursor interaction required).
 * – Decorations are recomputed only when the document content changes (`tr.docChanged`).
 * – All variables from the `variableNames` set are highlighted simultaneously.
 *
 * @param variableNames - The set of known variable names (same set used by the language parser).
 */
export function variableDefaultHighlight(variableNames: ReadonlySet<string>): Extension {
  return StateField.define<DecorationSet>({
    create(state: EditorState): DecorationSet {
      return buildDefaultDecorations(state.doc.toString(), variableNames);
    },

    update(decorations: DecorationSet, tr): DecorationSet {
      // Only recompute when the document itself changed
      if (!tr.docChanged) {
        return decorations;
      }
      return buildDefaultDecorations(tr.state.doc.toString(), variableNames);
    },

    provide(field): Extension {
      return EditorView.decorations.from(field);
    }
  });
}
